import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LoadingState,
  ErrorState,
  EmptyState,
  StrengthBar,
  AttentionBadge,
  AskAboutThis,
  beliefSentence,
  healthStatus,
} from "@/components/research-hub";
import { useCioThesisHealth } from "@/hooks/useResearchHubCio";

type Row = Record<string, any>;
const HUB = "/partner/research-hub";

// Statuses that mean "this view wants a look" — these float to the top under "by attention".
const NEEDS_ATTENTION = new Set(["live_contradiction", "stale", "single_source"]);

type SortMode = "attention" | "conviction";

function attentionRank(r: Row): number {
  const status = (r.health_status ?? "").toString().toLowerCase();
  return NEEDS_ATTENTION.has(status) ? 0 : 1;
}

export default function CioThesisHealth() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useCioThesisHealth(0, 100);
  const [sort, setSort] = useState<SortMode>("attention");

  const rows: Row[] = Array.isArray(data) ? data : [];

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const convA = typeof a.conviction_score === "number" ? a.conviction_score : 0;
      const convB = typeof b.conviction_score === "number" ? b.conviction_score : 0;
      if (sort === "attention") {
        const rankDiff = attentionRank(a) - attentionRank(b);
        if (rankDiff !== 0) return rankDiff;
      }
      // both modes fall back to conviction, strongest first
      return convB - convA;
    });
    return copy;
  }, [rows, sort]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Views under pressure</h1>
          <p className="font-body text-sm text-muted-foreground mt-1">
            Every house view, with the ones that need a look first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-body text-xs text-muted-foreground whitespace-nowrap">Sort</span>
          <Select value={sort} onValueChange={(v) => setSort(v as SortMode)}>
            <SelectTrigger className="w-44 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="attention">By attention</SelectItem>
              <SelectItem value="conviction">By conviction</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <LoadingState rows={6} />
      ) : error ? (
        <ErrorState onRetry={() => refetch()} />
      ) : sorted.length === 0 ? (
        <EmptyState title="Every view looks healthy right now." tone="good" />
      ) : (
        <div className="space-y-2">
          {sorted.map((r, i) => (
            <ThesisHealthRow
              key={r.edge_id ?? i}
              r={r}
              onOpen={(id) => navigate(`${HUB}/thesis/${id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ThesisHealthRow({ r, onOpen }: { r: Row; onOpen: (id: string) => void }) {
  const sentence = beliefSentence(r.from_label, r.to_label, r.thesis_direction);
  const hs = healthStatus(r.health_status);
  const staleness = r.staleness_days ?? r.days_since_evidence;
  const hasEdge = typeof r.edge_id === "string" && r.edge_id.length > 0;

  return (
    <Card className="p-3 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1 space-y-2">
        <button
          type="button"
          disabled={!hasEdge}
          onClick={() => hasEdge && onOpen(r.edge_id)}
          className="block w-full text-left font-body text-sm truncate text-foreground hover:text-primary transition-colors disabled:cursor-default disabled:hover:text-foreground"
        >
          {sentence}
        </button>
        <div className="flex items-center gap-x-3 gap-y-1 flex-wrap">
          <StrengthBar value={r.conviction_score} />
          {r.single_source_risk && (
            <span className="font-body text-[11px] text-muted-foreground">one source</span>
          )}
          {staleness != null && (
            <span className="font-body text-[11px] text-muted-foreground">
              evidence {staleness} days old
            </span>
          )}
        </div>
        {(r.drift_alert_id != null || r.divergent_against_edge_id != null) && (
          <div className="flex items-center gap-2 flex-wrap">
            {r.drift_alert_id != null && (
              <AttentionBadge label="the market is moving against this" tone="bad" />
            )}
            {r.divergent_against_edge_id != null && (
              <AttentionBadge label="another of our views points the other way" tone="attention" />
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <AttentionBadge label={hs.label} tone={hs.tone} />
        <AskAboutThis prefill={`Tell me more about our view that ${sentence}.`} label="" />
      </div>
    </Card>
  );
}
